import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, PublicOnlyRoute } from "@/components/ProtectedRoute";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import MyCommandCenter from "@/pages/MyCommandCenter";
import BuyerNeeds from "@/pages/BuyerNeeds";
import HaveBuyer from "@/pages/HaveBuyer";
import BuyerMatches from "@/pages/BuyerMatches";
import HaveProperty from "@/pages/HaveProperty";
import PropertyMatches from "@/pages/PropertyMatches";
import PropertyDetail from "@/pages/PropertyDetail";
import Collaborations from "@/pages/Collaborations";
import CollaborationRoom from "@/pages/CollaborationRoom";
import Profile from "@/pages/Profile";
import MyProperties from "@/pages/MyProperties";
import MyBuyers from "@/pages/MyBuyers";
import Opportunities from "@/pages/Opportunities";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Connections from "@/pages/Connections";
import Messages from "@/pages/Messages";
import Notifications from "@/pages/Notifications";
import CommunicationDetail from "@/pages/CommunicationDetail";
import NotFound from "@/pages/NotFound";
import Admin from "@/pages/Admin";
import AdminAds from "@/pages/AdminAds";
import Professionals from "@/pages/Professionals";
import LookingForProperty from "@/pages/LookingForProperty";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import VerifyRequired from "@/pages/VerifyRequired";
import Marketplace from "@/pages/Marketplace";
import PublicListingDetail from "@/pages/PublicListingDetail";
import PublicProfile from "@/pages/PublicProfile";
import PublicOrganization from "@/pages/PublicOrganization";
import MyOrganizations from "@/pages/MyOrganizations";
import PromoteProfile from "@/pages/PromoteProfile";
import EditOrganization from "@/pages/EditOrganization";
import RecruitmentDetail from "@/pages/RecruitmentDetail";
import PostRecruitment from "@/pages/PostRecruitment";
import MyRecruitment from "@/pages/MyRecruitment";
import RecruitmentApplicants from "@/pages/RecruitmentApplicants";
import QrResolver from "@/pages/QrResolver";
import MyCard from "@/pages/MyCard";
import PublicBuyerNeedDetail from "@/pages/PublicBuyerNeedDetail";
import ConsumerHome from "@/pages/ConsumerHome";
import ConsumerRequirement from "@/pages/ConsumerRequirement";
import ConsumerMatches from "@/pages/ConsumerMatches";
import Inquiries from "@/pages/Inquiries";
import LegalPage from "@/pages/LegalPage";
import SellLanding from "@/pages/SellLanding";
import SellSubmit from "@/pages/SellSubmit";
import Advertise from "@/pages/Advertise";
import About from "@/pages/About";
import Help from "@/pages/Help";
import Contact from "@/pages/Contact";
import Referrals from "@/pages/Referrals";
import PostEvent from "@/pages/PostEvent";
import EventDetail from "@/pages/EventDetail";
import MyEvents from "@/pages/MyEvents";
import Landing from "@/pages/Landing";
import { PC_MARK } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";

const P = ({ children, adminOnly, proOnly, verifiedProOnly }) => <ProtectedRoute adminOnly={adminOnly} proOnly={proOnly} verifiedProOnly={verifiedProOnly}>{children}</ProtectedRoute>;

function Home() {
  const { user } = useAuth();
  const at = user?.account_type || "PROFESSIONAL";
  if (at === "CONSUMER") return <ConsumerHome />;
  if (at === "REFERRAL_PARTNER") return <Navigate to="/referrals" replace />;
  return <Dashboard />;
}

// Root gate: anonymous visitors get the public, indexable Landing; authenticated users
// keep the EXACT existing protected Home (verify/role gates + noindex preserved via <P>).
function RootGate() {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <img src={PC_MARK} alt="PropertyConnect" className="h-16 w-auto animate-pulse" />
      </div>
    );
  }
  if (!user) return <Landing />;
  return <P><Home /></P>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-center" richColors />
        <AnalyticsTracker />
        <Routes>
          <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
          <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
          <Route path="/looking-for-property" element={<LookingForProperty />} />
          <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-required" element={<VerifyRequired />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/listing/:id" element={<PublicListingDetail />} />
          <Route path="/pro/:slug" element={<PublicProfile />} />
          <Route path="/professionals" element={<Professionals />} />
          <Route path="/org/:slug" element={<PublicOrganization />} />
          <Route path="/recruitment/:id" element={<RecruitmentDetail />} />
          <Route path="/q/:publicId" element={<QrResolver />} />
          <Route path="/buyer-need/:id" element={<PublicBuyerNeedDetail />} />
          <Route path="/event/:id" element={<EventDetail />} />
          <Route path="/sell" element={<SellLanding />} />
          <Route path="/sell/new" element={<P><SellSubmit /></P>} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/about" element={<About />} />
          <Route path="/help" element={<Help />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/advertise" element={<P proOnly><Advertise /></P>} />
          <Route path="/" element={<RootGate />} />
          <Route path="/command-center" element={<P><MyCommandCenter /></P>} />
          <Route path="/buyer-needs" element={<P><BuyerNeeds /></P>} />
          <Route path="/requirements/new" element={<P><ConsumerRequirement /></P>} />
          <Route path="/requirements/:id/edit" element={<P><ConsumerRequirement /></P>} />
          <Route path="/requirements/:id/matches" element={<P><ConsumerMatches /></P>} />
          <Route path="/have-buyer" element={<P verifiedProOnly><HaveBuyer /></P>} />
          <Route path="/buyer/:id/matches" element={<P verifiedProOnly><BuyerMatches /></P>} />
          <Route path="/have-property" element={<P verifiedProOnly><HaveProperty /></P>} />
          <Route path="/property/:id/matches" element={<P verifiedProOnly><PropertyMatches /></P>} />
          <Route path="/property/:id" element={<P verifiedProOnly><PropertyDetail /></P>} />
          <Route path="/collaborations" element={<P verifiedProOnly><Collaborations /></P>} />
          <Route path="/collaboration/:id" element={<P verifiedProOnly><CollaborationRoom /></P>} />
          <Route path="/profile" element={<P proOnly><Profile /></P>} />
          <Route path="/my-card" element={<P proOnly><MyCard /></P>} />
          <Route path="/my-properties" element={<P proOnly><MyProperties /></P>} />
          <Route path="/my-buyers" element={<P proOnly><MyBuyers /></P>} />
          <Route path="/opportunities" element={<P verifiedProOnly><Opportunities /></P>} />
          <Route path="/leads" element={<P verifiedProOnly><Leads /></P>} />
          <Route path="/leads/:id" element={<P verifiedProOnly><LeadDetail /></P>} />
          <Route path="/referrals" element={<P><Referrals /></P>} />
          <Route path="/post-event" element={<P verifiedProOnly><PostEvent /></P>} />
          <Route path="/post-recruitment" element={<P verifiedProOnly><PostRecruitment /></P>} />
          <Route path="/post-recruitment/:id/edit" element={<P verifiedProOnly><PostRecruitment /></P>} />
          <Route path="/my-recruitment" element={<P verifiedProOnly><MyRecruitment /></P>} />
          <Route path="/recruitment/:id/applicants" element={<P verifiedProOnly><RecruitmentApplicants /></P>} />
          <Route path="/my-events" element={<P verifiedProOnly><MyEvents /></P>} />
          <Route path="/my-organizations" element={<P verifiedProOnly><MyOrganizations /></P>} />
          <Route path="/promote-profile" element={<P verifiedProOnly><PromoteProfile /></P>} />
          <Route path="/organizations/:id/edit" element={<P verifiedProOnly><EditOrganization /></P>} />
          <Route path="/connections" element={<P verifiedProOnly><Connections /></P>} />
          <Route path="/messages" element={<P verifiedProOnly><Messages /></P>} />
          <Route path="/inquiries" element={<P proOnly><Inquiries /></P>} />
          <Route path="/notifications" element={<P><Notifications /></P>} />
          <Route path="/communications/:id" element={<P><CommunicationDetail /></P>} />
          <Route path="/admin" element={<P adminOnly><Admin /></P>} />
          <Route path="/admin/communications" element={<P adminOnly><Admin initialSection="communications" /></P>} />
          <Route path="/admin/spotlight" element={<P adminOnly><AdminAds /></P>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
